"use client";

import { useEffect, useState, useContext } from "react";
import { useSelector } from "react-redux";
import summaryApi from "../common/index";
import { Link } from "react-router-dom";
import { FaRegUserCircle } from "react-icons/fa";
import { toast } from "react-hot-toast";
import Context from "../context";
import "./Wishlist.css";

const Wishlist = () => {
  const [data, setData] = useState([]);
  const [clearConfirmation, setClearConfirmation] = useState(false);
  const [removeConfirmation, setRemoveConfirmation] = useState(null);

  const user = useSelector((state) => state.user.user);
  const { fetchUserWishlistCount } = useContext(Context);

  const fetchData = async () => {
    try {
      const response = await fetch(summaryApi.getWishlistProducts.url, {
        method: summaryApi.getWishlistProducts.method,
        credentials: "include",
        headers: { "content-type": "application/json" },
      });

      const responseData = await response.json();
      if (responseData.success) {
        setData(responseData.data || []);
      } else {
        toast.error("Failed to fetch wishlist products");
      }
    } catch (error) {
      toast.error("Error fetching wishlist products");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const removeFromWishlist = async (id) => {
    try {
      const response = await fetch(summaryApi.removeWishlistProduct.url, {
        method: summaryApi.removeWishlistProduct.method,
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ productId: id }),
      });

      const responseData = await response.json();

      if (responseData.success) {
        await fetchUserWishlistCount();
        fetchData();
        toast.success("Item removed from wishlist");
      } else {
        toast.error(responseData.message);
      }
    } catch (error) {
      toast.error("Error removing item from wishlist");
    }
  };

  const clearWishlist = async () => {
    try {
      for (const product of data) {
        await fetch(summaryApi.removeWishlistProduct.url, {
          method: summaryApi.removeWishlistProduct.method,
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ productId: product?.productId }),
        });
      }
      await fetchUserWishlistCount();
      fetchData();
      toast.success("Wishlist cleared");
    } catch (error) {
      toast.error("Error clearing wishlist");
    }
    setClearConfirmation(false);
  };

  return (
    <div className="container mx-auto min-h-screen bg-gray-50 p-4">
      <h1 className="text-3xl font-bold text-center mb-6">WISHLIST</h1>

      <div className="flex justify-between items-center border-b border-gray-300 pb-4 mb-6">
        <h2 className="text-xl font-semibold">My Wishlist</h2>
        {user?._id ? (
          <div className="w-10 h-10 rounded-full overflow-hidden">
            {user.profilePic ? (
              <img
                src={
                  user.profilePic.startsWith("http")
                    ? user.profilePic
                    : `https://dates-website-backend.vercel.app/${user.profilePic}`
                }
                alt={user.name || "User"}
                className="w-10 h-10 object-cover rounded-full border border-gray-300"
              />
            ) : (
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <FaRegUserCircle className="w-6 h-6 text-gray-600" />
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <FaRegUserCircle className="w-10 h-10 text-gray-600" />
            <span className="text-gray-700 font-medium">Guest</span>
          </div>
        )}
      </div>

      {data.length === 0 ? (
        <div className="text-center mt-20">
          <p className="font-bold text-gray-900 text-lg mb-2">
            Love a product? Add it to your wishlist.
          </p>
          <p className="text-gray-500 mb-6">
            Your wish list is still empty. Add items by clicking the star icon.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-black text-white font-bold rounded-md hover:bg-gray-800 transition"
          >
            Continue Shopping
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {data.map((product) => (
            <div
              key={product?.productId}
              className="flex gap-4 p-4 border border-gray-300 rounded-lg shadow-sm items-center"
            >
              <Link
                to={`/product/${product?.productId}`}
                className="flex gap-4 flex-1 items-center hover:opacity-80 transition-opacity"
              >
                <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden">
                  <img
                    src={product?.productImage || ""}
                    alt={product?.productName || "Product"}
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-lg">
                    {product?.productName}
                  </h3>
                  <p className="text-gray-600 capitalize">
                    {product?.category || "-"}
                  </p>
                  <p className="font-medium text-gray-800 mt-2">
                    ${product?.priceRs?.toFixed(2) || "0.00"}
                  </p>

                  {product?.sizes && product.sizes.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600">
                        Available sizes: {product.sizes.join(", ")}
                      </p>
                    </div>
                  )}
                </div>
              </Link>

              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setRemoveConfirmation(product?.productId)}
                  className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-3 py-1 rounded-md"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {clearConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">
              Confirm Clear Wishlist
            </h2>
            <p className="mb-4">Are you sure you want to clear all items?</p>
            <div className="flex justify-end gap-4">
              <button
                className="bg-gray-200 text-gray-700 hover:bg-gray-300 py-2 px-4 rounded-lg"
                onClick={() => setClearConfirmation(false)}
              >
                Cancel
              </button>
              <button
                className="bg-black text-white hover:bg-gray-800 py-2 px-4 rounded-lg"
                onClick={clearWishlist}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {removeConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-xl font-semibold mb-4">Confirm Remove</h2>
            <p className="mb-4">Are you sure you want to remove this item?</p>
            <div className="flex justify-end gap-4">
              <button
                className="bg-gray-200 text-gray-700 hover:bg-gray-300 py-2 px-4 rounded-lg"
                onClick={() => setRemoveConfirmation(null)}
              >
                Cancel
              </button>
              <button
                className="bg-black text-white hover:bg-gray-800 py-2 px-4 rounded-lg"
                onClick={() => {
                  removeFromWishlist(removeConfirmation);
                  setRemoveConfirmation(null);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wishlist;
